import { toast } from "react-toastify";
import { sendWebSocketMessage } from '../hooks/useWebSocket';
import {
  getPayloadSize,
  payloadQualifiesForS3Upload,
} from "../component/Methods/checks";
import { findLivePriceForTicker } from './livePriceTickerMatch';
import { uploadToS3 } from "./s3Utils";

function isDateOlderThanThreshold(dateString, threshold) {
  const currentDate = new Date();
  const givenDate = new Date(dateString);
  const timeDifference = currentDate - givenDate;
  const minutesDifference = timeDifference / (1000 * 60);
  return minutesDifference > threshold;
}

/**
 * Sends market_data over websocket, uploading to S3 when payload is large.
 * @returns {Promise<boolean>} whether the request was sent
 */
export async function sendMarketDataRequest(dataPayload, apiInfo, userInfo) {
  const socketRequest = {
    action: "market_data",
    data: dataPayload,
  };

  if (!payloadQualifiesForS3Upload(getPayloadSize(socketRequest))) {
    sendWebSocketMessage(JSON.stringify(socketRequest));
    return true;
  }

  try {
    const s3Result = await uploadToS3(
      socketRequest,
      apiInfo,
      userInfo,
      "market_data"
    );

    if (!s3Result?.success) {
      toast.error("Failed to upload market data to S3. Please try again later.");
      return false;
    }

    sendWebSocketMessage(
      JSON.stringify({
        action: "market_data",
        file: s3Result.fileName,
      })
    );
    return true;
  } catch {
    toast.error("Failed to upload market data to S3. Please try again later.");
    return false;
  }
}

function buildMarketDataPayload({
  thresholdMinutes,
  tickers,
  fields,
  market,
  requestPerformance = false,
  apiHit = true,
}) {
  const payload = {
    threshold: thresholdMinutes * 60,
    request: true,
    fields,
    tickers,
  };

  if (requestPerformance) return payload;

  const marketName = String(market || "bloomberg").toLowerCase();
  if (marketName === "factset") {
    payload.market = "factset";
  } else if (marketName === "fmp") {
    payload.market = "fmp";
    payload.request = Boolean(apiHit);
  } else if (marketName === "yahoofinance") {
    payload.market = "yahoofinance";
    payload.request = Boolean(apiHit);
  }

  return payload;
}

export async function getData(tickers, threshold, sendRequest = true, sendBenchmarks = true, requestPerformance = false, market = "bloomberg", livePrices, apiHit = true, apiInfo, userInfo) {
  let existingData = livePrices
  let socketRequest = [];
  let deltaRequests = [];
  let benchMarks = [];
  let toSend = {
    data: [],
    requested: tickers.length,
    found: 0,
    expired: 0,
    queried: 0,
  };

  const isYahooFinance = String(market || "").toLowerCase() === "yahoofinance";

  const pushYahooTicker = (ticker) => {
    if (!ticker?.symbol) return;
    socketRequest.push({
      symbol: ticker.symbol,
      investment: ticker.investment,
      exchange: ticker.exchange,
      currency: ticker.currency,
      ...(ticker.stock && { stock: ticker.stock }),
    });
    toSend.queried++;
  };

  benchMarks.push( { symbol: "qqq", investment: 4, exchange: 4, currency: 7 }, { symbol: "spy", investment: 4, exchange: 4, currency: 7 },  { symbol: "iwm", investment: 4, exchange: 4, currency: 7 }, { symbol: "dia", investment: 4, exchange: 4, currency: 7 },{ symbol: "iyk", investment: 4, exchange: 4, currency: 7 }, { symbol: "kre", investment: 4, exchange: 4, currency: 7 }, { symbol: "kbwb", investment: 4, exchange: 4, currency: 7 });

  tickers.map((ticker) => {
    if (isYahooFinance) {
      toSend.data.push({
        ticker: ticker.symbol,
        last: 1,
        current: 1,
        delta: 1,
      });
      pushYahooTicker(ticker);
      return;
    }

    if (existingData.length > 0) {
      const ed = existingData;
      const found = findLivePriceForTicker(existingData, ticker);

      if (found) {
        toSend.found++;
        if (isDateOlderThanThreshold(found.expiration, threshold)) {
          socketRequest.push({
            symbol: ticker.symbol,
            investment: 4,
            exchange: ticker.exchange,
            currency: ticker.currency,
            ...(ticker.stock && { stock: ticker.stock })
          });

          if (ticker.investment == 10) {
            deltaRequests.push({
              symbol: ticker.symbol,
              investment: 9,
              currency: 7,
              exchange: 4,
              underlying: {
                investment: 4,
                symbol: ticker.symbol,
                currency: 7,
                exchange: 4,
                ...(ticker.stock && { stock: ticker.stock })
              },
              strikePrice: ticker.strikePrice,
              expirationDate: ticker.expirationDate,
              option: ticker.option,
            });
          }

          toSend.expired++;
          toSend.queried++;
        }

        toSend.data.push({
          ticker: found.symbol,
          last: found.yestPrice,
          current: found.price,
          delta: 1,
          date: found.expiration,
        });
      } else {
        //IF NOT FOUND IN LOCAL STORAGE, SEND DUMMY DATA AND THEN SEND A WEBSOCKET REQUEST TO FETCH
        toSend.data.push({
          ticker: ticker.symbol,
          last: 1,
          current: 1,
          delta: 1,
        });

        if (ticker.investment == 4 || ticker.investment == 10) {
          socketRequest.push({
            symbol: ticker.symbol,
            investment: 4,
            exchange: ticker.exchange,
            currency: ticker.currency,
            ...(ticker.stock && { stock: ticker.stock })
          });

          if (ticker.investment == 10) {
            deltaRequests.push({
              symbol: ticker.symbol,
              investment: 9,
              currency: 7,
              exchange: 4,
              underlying: {
                investment: 4,
                symbol: ticker.symbol,
                currency: 7,
                exchange: 4,
                ...(ticker.stock && { stock: ticker.stock })
              },
              strikePrice: ticker.strikePrice,
              expirationDate: ticker.expirationDate,
              option: ticker.option,
            });
          }

          toSend.queried++;
        }
      }
    } else {
      //IF THERE IS NOTHING IN LOCAL STORAGE, SEND DUMMY DATA AND THEN SEND A WEBSOCKET REQUEST TO FETCH
      toSend.data.push({
        ticker: ticker.symbol,
        last: 1,
        current: 1,
        delta: 1,
      });
      if (ticker.investment == 4 || ticker.investment == 9) {
        socketRequest.push({
          symbol: ticker.symbol,
          investment: 4,
          exchange: ticker.exchange,
          currency: ticker.currency,
          ...(ticker.stock && { stock: ticker.stock })
        });
        toSend.queried++;

        if (ticker.investment == 10) {
          deltaRequests.push({
            symbol: ticker.symbol,
            investment: 9,
            currency: 7,
            exchange: 4,
            underlying: {
              investment: 4,
              symbol: ticker.symbol,
              currency: 7,
              exchange: 4,
              ...(ticker.stock && { stock: ticker.stock })
            },
            strikePrice: ticker.strikePrice,
            expirationDate: ticker.expirationDate,
            option: ticker.option,
          });
        }
      }
    }
  });

  if (sendBenchmarks && !isYahooFinance) {
   
    benchMarks.map((ticker) => {
    
        const found = findLivePriceForTicker(existingData, ticker);
        if (found) {
          if (isDateOlderThanThreshold(found.expiration)) {
            socketRequest.push({
              symbol: ticker.symbol,
              investment: ticker.investment,
              exchange: ticker.exchange,
              currency: ticker.currency,
            });
          }
          toSend.data.push({
            ticker: found.symbol,
            last: found.yestPrice,
            current: found.price,
            delta: 1,
            date: found.expiration,
          });
        } else {
          toSend.data.push({
            ticker: ticker.symbol,
            last: 1,
            current: 1,
            delta: 1,
          });

          socketRequest.push({
            symbol: ticker.symbol,
            investment: ticker.investment,
            exchange: ticker.exchange,
            currency: ticker.currency,
          });
        }
      
    });
  }

  if (socketRequest.length > 0 && sendRequest) {
    await sendMarketDataRequest(
      buildMarketDataPayload({
        thresholdMinutes: threshold,
        tickers: socketRequest,
        fields: requestPerformance ? [4, 5, 6, 7, 8, 11] : [9, 10],
        market,
        requestPerformance,
        apiHit,
      }),
      apiInfo,
      userInfo
    );
  }

  if (deltaRequests.length > 0 && sendRequest) {
    await sendMarketDataRequest(
      buildMarketDataPayload({
        thresholdMinutes: threshold,
        tickers: deltaRequests,
        fields: [3],
      }),
      apiInfo,
      userInfo
    );
  }

  return [toSend.data, socketRequest.length, sendRequest];
}
